import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@ygtechnologies/rubus-ui';
import { CustomLink } from '@ygtechnologies/bygr-frontend-component-library';
import PrimaryCard from '@/components/Statistics/PrimaryCard';
import { useAsyncState } from '@/utils/hooks';
import { getCurrentUser, hasAccessLevel } from '@/services/auth';
import { EmailTemplateScopeSummary, getEmailTemplateScopeSummary } from '@/services/emailTemplates';
import { languageLabel } from './shared';
import theme from './EmailTemplates.module.scss';

interface EmailTemplatesCardProps {
    /** Exactly one of the two — the scope this dashboard is about. */
    projectId?: number;
    profileId?: number;
}

/**
 * What this scope's mails are actually laid out with. "Custom" only when the scope has its own
 * override; a project inheriting its seller profile's custom layout says so instead of "Default".
 */
function describeLayout(summary: EmailTemplateScopeSummary): string {
    if (summary.layoutOverrideLanguages.length > 0) {
        return `Custom (${summary.layoutOverrideLanguages.map(languageLabel).join(', ')})`;
    }
    if (summary.layoutSource === 'profile-override') {
        return 'From the seller profile';
    }
    return 'Default layout';
}

/**
 * Dashboard card summarizing the email template overrides at one scope, with deep links into the
 * editor (the links carry only ids — the editor resolves the names). Renders nothing for users below
 * Advanced, mirroring the backend policy on the email-templates endpoints.
 */
const EmailTemplatesCard: React.FC<EmailTemplatesCardProps> = ({ projectId, profileId }) => {
    // undefined = still loading, null = not visible to this user.
    const [summary] = useAsyncState<EmailTemplateScopeSummary | null | undefined>(
        async () => {
            const user = await getCurrentUser();
            if (!hasAccessLevel(user.accessLevel, 'Advanced')) {
                return null;
            }
            return getEmailTemplateScopeSummary({ projectId, profileId });
        },
        [projectId, profileId],
        undefined
    );

    if (summary === null) {
        return null;
    }

    const scopeSearch = projectId ? `projectId=${projectId}` : `profileId=${profileId}`;

    const contentUrl = (name: string, languageCode: string | null) =>
        `/email-templates/contents/${encodeURIComponent(name)}?${scopeSearch}` +
        (languageCode ? `&language=${encodeURIComponent(languageCode)}` : '');

    return (
        <PrimaryCard
            title="Email templates"
            loading={!summary}
            actions={
                <Button as={Link} to="/email-templates/contents" size="xs" variant="tertiary">
                    All templates
                </Button>
            }>
            {() => (
                <>
                    <PrimaryCard.Row label="Layout">
                        <CustomLink to={`/email-templates/layouts?${scopeSearch}`}>
                            {describeLayout(summary!)}
                        </CustomLink>
                    </PrimaryCard.Row>
                    <PrimaryCard.Row label="Content overrides">
                        {summary!.contentOverrides.length === 0 ? (
                            'None'
                        ) : (
                            <div className={theme.overrideLinkList}>
                                {summary!.contentOverrides.map((x) => (
                                    <CustomLink
                                        key={`${x.name}|${x.languageCode ?? ''}`}
                                        to={contentUrl(x.name, x.languageCode)}>
                                        {x.name}
                                        {x.languageCode ? ` — ${x.languageCode}` : ''}
                                    </CustomLink>
                                ))}
                            </div>
                        )}
                    </PrimaryCard.Row>
                </>
            )}
        </PrimaryCard>
    );
};

export default EmailTemplatesCard;
